import { useId, useState } from 'react';
import { Badge, Button, Field, Input, Modal, Select } from '../../../components/ui';
import type { CompanySummary } from '../../../types';
import { checarEntrega, somenteDominio } from '../entrega';
import type { EmailCampaign, EmailDomainHealth } from '../types';
import { useFeedbackTemporario } from '../useFeedbackTemporario';
import { DNS_CHECK_LABEL, DNS_CHECK_TONE } from '../vocabulario';
import { ChecklistDeEntrega } from './ChecklistDeEntrega';

/**
 * Teste de entrega DETALHADO do canal de e-mail.
 *
 * Decisoes nao obvias:
 *  1. Testa por EMPRESA, porque o dominio remetente e dela: SPF, DKIM e
 *     DMARC de "escolafluente.exemplo" nao dizem nada sobre a FitPro.
 *  2. Opcionalmente cruza com o CONTEUDO de uma campanha da empresa
 *     (assunto, links, personalizacao). Sem campanha, so o dominio.
 *  3. "Enviar teste" registra o pedido na previa -- nada sai enquanto o
 *     provedor nao for escolhido, e a tela diz isso.
 */
export function ModalTesteEntrega({
  open,
  onClose,
  companies,
  companyIdPadrao,
  domains,
  campanhas,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly companies: readonly CompanySummary[];
  readonly companyIdPadrao: string;
  readonly domains: readonly EmailDomainHealth[];
  readonly campanhas: readonly EmailCampaign[];
}): JSX.Element | null {
  const campanhaId = useId();
  const [companyId, setCompanyId] = useState(companyIdPadrao);
  const [conteudoId, setConteudoId] = useState<string>('');
  const [email, setEmail] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const { mensagem, mostrar } = useFeedbackTemporario();

  const dominio = domains.find((d) => d.companyId === companyId) ?? null;
  const daEmpresa = campanhas.filter((c) => c.companyId === companyId);
  const campanha = daEmpresa.find((c) => c.id === conteudoId) ?? null;
  const completo = checarEntrega(
    campanha === null ? { subject: '', preheader: '', body: '', ctaLabel: '', ctaUrl: '' } : { subject: campanha.subject, preheader: campanha.preheader, body: campanha.body, ctaLabel: campanha.ctaLabel, ctaUrl: campanha.ctaUrl },
    dominio,
  );
  const resultado = campanha === null ? somenteDominio(completo) : completo;

  const enviar = (): void => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setErro('Informe um e-mail válido.');
      return;
    }
    setErro(null);
    mostrar(`Teste registrado para ${email.trim()} — nada sai enquanto o provedor não for definido.`);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title="Teste de entrega"
      description="Autenticação do domínio da empresa e, se quiser, o conteúdo de uma campanha: o que ajuda e o que atrapalha chegar na caixa principal."
      footer={
        <Button variant="secondary" onClick={onClose}>
          Fechar
        </Button>
      }
    >
      <div className="flex flex-col gap-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Empresa" hint={dominio === null ? 'Domínio ainda não cadastrado.' : `Domínio remetente: ${dominio.domain}`}>
            {({ id, describedBy }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                value={companyId}
                onChange={(e) => {
                  setCompanyId(e.target.value);
                  setConteudoId('');
                }}
              >
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Conteúdo a testar" htmlFor={campanhaId} hint="Opcional: cruza o domínio com o texto de uma campanha.">
            {({ id, describedBy }) => (
              <Select id={id} aria-describedby={describedBy} value={conteudoId} onChange={(e) => setConteudoId(e.target.value)}>
                <option value="">Só o domínio</option>
                {daEmpresa.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.subject}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>

        {dominio === null ? null : (
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-ink-subtle">DNS de {dominio.domain}:</span>
            <Badge tone={DNS_CHECK_TONE[dominio.spf]} dot>
              SPF {DNS_CHECK_LABEL[dominio.spf]}
            </Badge>
            <Badge tone={DNS_CHECK_TONE[dominio.dkim]} dot>
              DKIM {DNS_CHECK_LABEL[dominio.dkim]}
            </Badge>
            <Badge tone={DNS_CHECK_TONE[dominio.dmarc]} dot>
              DMARC {DNS_CHECK_LABEL[dominio.dmarc]}
              {dominio.dmarcPolicy === null ? '' : ` (p=${dominio.dmarcPolicy})`}
            </Badge>
          </div>
        )}

        <ChecklistDeEntrega resultado={resultado} />

        <Field label="Mandar um teste para" hint="Um endereço da equipe. Na prévia nada é enviado." error={erro}>
          {({ id, describedBy, invalid }) => (
            <div className="flex gap-2">
              <Input
                id={id}
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
                type="email"
                inputMode="email"
                autoComplete="off"
                placeholder="voce@empresa.exemplo"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    enviar();
                  }
                }}
              />
              <Button variant="primary" size="lg" icon="email" onClick={enviar}>
                Enviar teste
              </Button>
            </div>
          )}
        </Field>

        {mensagem === null ? null : (
          <p role="status" className="flex flex-wrap items-center gap-2 text-sm text-ink">
            <Badge tone="warning" dot>
              Simulado na prévia
            </Badge>
            {mensagem}
          </p>
        )}
      </div>
    </Modal>
  );
}
